import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import AccountBalanceWalletOutlined from "@mui/icons-material/AccountBalanceWalletOutlined";
import BarChartRounded from "@mui/icons-material/BarChartRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import GroupsOutlined from "@mui/icons-material/GroupsOutlined";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import SupportAgentOutlined from "@mui/icons-material/SupportAgentOutlined";
import TrackChangesOutlined from "@mui/icons-material/TrackChangesOutlined";

const commandBackground = "/img-sala-de-comando-fundo.png";
const refreshPollingIntervalMs = 3000;
const refreshPollingMaxAttempts = 12;
const COBRANCA_PRODUCT_ID = 6;

const colors = {
  page: "var(--cc-page)",
  panel: "var(--cc-panel)",
  panelSoft: "var(--cc-panel-soft)",
  border: "var(--cc-border)",
  borderStrong: "var(--cc-border-strong)",
  text: "var(--cc-text)",
  muted: "var(--cc-muted)",
  subtle: "var(--cc-subtle)",
  primary: "var(--cc-primary)",
  primarySoft: "var(--cc-primary-soft)",
  chartPrimary: "var(--cc-chart-primary)",
  chartSecondary: "var(--cc-chart-secondary)",
  chartMuted: "var(--cc-chart-muted)",
  chartTrack: "var(--cc-chart-track)",
  danger: "var(--cc-danger)",
  success: "var(--cc-success)",
};

const summaryGradients = {
  royal: "linear-gradient(135deg, #5069f5 0%, #2855df 100%)",
  purple: "linear-gradient(135deg, #5b3bea 0%, #6a27dc 100%)",
  teal: "linear-gradient(135deg, #119bb5 0%, #05759e 100%)",
  green: "linear-gradient(135deg, #14976b 0%, #08734f 100%)",
  navy: "linear-gradient(135deg, #26354d 0%, #111b30 100%)",
};

const moduleVolume = [
  { label: "Pré Venda", value: 1248, display: "1.248 atendimentos", color: colors.chartPrimary },
  { label: "Inadimplência", value: 1120, display: "1.120 msgs enviadas", color: colors.chartSecondary },
  { label: "Pós-Venda", value: 856, display: "856 atendimentos", color: colors.chartSecondary },
  { label: "Corretores", value: 42, display: "42 msgs", color: colors.chartMuted },
  { label: "Conteúdo", value: 26, display: "26 msgs", color: colors.chartMuted },
];

const commandCenterProducts = [
  { idProduto: 2, label: "Pré Venda", color: colors.chartPrimary },
  { idProduto: 1, label: "Corretor", color: colors.chartSecondary },
  { idProduto: 5, label: "Pós-vendas", color: colors.chartSecondary },
  { idProduto: COBRANCA_PRODUCT_ID, label: "Inadimplência", color: colors.chartMuted },
  { idProduto: 9, label: "Contabilidade", color: colors.chartMuted },
  { idProduto: 3, label: "Conteúdo", color: colors.chartMuted },
];

const preAttendanceFunnel = [
  { label: "Atendimentos", value: 1248, display: "1.248", color: colors.chartPrimary },
  { label: "Qualificados", value: 312, display: "312", color: colors.chartSecondary },
  { label: "Descartados", value: 74, display: "74", color: colors.chartMuted },
];

const postSaleTickets = [
  { label: "Concluídos", value: 724, color: colors.chartPrimary },
  { label: "Abertos", value: 32, color: colors.chartTrack },
];

const getAuthToken = () => {
  const rawToken =
    localStorage.getItem("jwtToken") ||
    localStorage.getItem("authToken") ||
    sessionStorage.getItem("token") ||
    "";

  return rawToken.startsWith("Bearer ") ? rawToken.slice(7) : rawToken;
};

const getProdutoId = (produto) =>
  Number(
    produto?.id_produto ??
      produto?.idProduto ??
      produto?.idProdutoAipim ??
      produto?.id_produto_aipim ??
      produto?.produto_id ??
      produto?.id
  );

const getProdutoNome = (produto) =>
  produto?.nome_produto ??
  produto?.nomeProduto ??
  produto?.produto ??
  produto?.nome ??
  "";

const buildContractedProductsSummary = (produtosContratados = []) => {
  const productNames = produtosContratados
    .map(getProdutoNome)
    .filter(Boolean);

  return {
    count: productNames.length,
    description: productNames.length > 0 ? productNames.join(", ") + "." : "Nenhuma operação ativa encontrada.",
  };
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatNumber = (value) =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(toNumber(value));

const formatPercent = (value) =>
  new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(toNumber(value));

const formatReferenceDate = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

const buildVolumeByContractedProducts = (produtosContratados = []) => {
  const contractsByProductId = new Map(
    produtosContratados.map((produto) => [getProdutoId(produto), produto])
  );

  const rows = commandCenterProducts.map((product) => {
    const contract = contractsByProductId.get(product.idProduto);

    if (!contract) {
      return {
        ...product,
        contracted: false,
        value: 0,
        limit: 0,
        progress: 0,
        display: "",
      };
    }

    const value =
      product.idProduto === 1
        ? toNumber(contract?.pessoas_cadastradas?.total)
        : toNumber(contract?.atendimentos ?? contract?.atendimentos_utilizados);
    const limit = toNumber(contract?.limite);
    const progress = limit > 0 ? Math.min((value / limit) * 100, 100) : 0;
    const isOverLimit = limit > 0 && value > limit;

    return {
      ...product,
      contracted: true,
      value,
      limit,
      progress,
      isOverLimit,
      display: `${formatNumber(value)} / ${formatNumber(limit)}`,
    };
  });

  return [
    ...rows.filter((item) => item.contracted),
    ...rows.filter((item) => !item.contracted),
  ];
};

const buildPreAttendanceFunnel = (produtosContratados = []) => {
  const preAttendanceContract = produtosContratados.find(
    (produto) => getProdutoId(produto) === 2
  );

  if (!preAttendanceContract) return [];

  const value = toNumber(preAttendanceContract?.atendimentos ?? preAttendanceContract?.atendimentos_utilizados);
  const limit = toNumber(preAttendanceContract?.limite);
  const progress = limit > 0 ? Math.min((value / limit) * 100, 100) : 0;
  const qualified = toNumber(preAttendanceContract?.leads_mes?.qualificados);
  const discarded = toNumber(preAttendanceContract?.leads_mes?.descartados);
  const qualificationRate = toNumber(preAttendanceContract?.leads_mes?.taxa_qualificacao);

  const funnel = [
    {
      ...preAttendanceFunnel[0],
      value,
      limit,
      progress,
      display: `${formatNumber(value)} / ${formatNumber(limit)}`,
    },
    {
      ...preAttendanceFunnel[1],
      value: qualified,
      display: formatNumber(qualified),
    },
    {
      ...preAttendanceFunnel[2],
      value: discarded,
      display: formatNumber(discarded),
    },
  ];

  funnel.qualificationRate = qualificationRate;

  return funnel;
};

const buildPostSaleTickets = (produtosContratados = []) => {
  const postSaleContract = produtosContratados.find(
    (produto) => getProdutoId(produto) === 5
  );

  if (!postSaleContract) return [];

  const concluded = toNumber(postSaleContract?.atendimentos_mes?.concluidos);
  const open = toNumber(postSaleContract?.atendimentos_mes?.abertos);

  return [
    {
      ...postSaleTickets[0],
      value: concluded,
    },
    {
      ...postSaleTickets[1],
      value: open,
    },
  ];
};

const getProductAttendances = (produto) => {
  const attendances = produto?.atendimentos ?? produto?.atendimentos_utilizados;

  if (attendances !== undefined && attendances !== null) {
    return toNumber(attendances);
  }

  return (
    toNumber(produto?.atendimentos_mes?.concluidos) +
    toNumber(produto?.atendimentos_mes?.abertos)
  );
};

const buildAttendanceSummaryByProduct = (produtosContratados = [], productId) => {
  const product = produtosContratados.find((produto) => getProdutoId(produto) === productId);
  const used = getProductAttendances(product);
  const contracted = toNumber(product?.limite);

  return {
    value: `${formatNumber(used)} / ${formatNumber(contracted)}`,
    progress: contracted > 0 ? Math.min((used / contracted) * 100, 100) : 0,
  };
};

const buildActiveConnectionsSummary = (produtosContratados = []) => {
  const contractedProductIds = new Set(
    produtosContratados.map(getProdutoId)
  );

  const hasCrmConnection = [2, 1, 5].some((idProduto) => contractedProductIds.has(idProduto));
  const hasSiengeConnection = [COBRANCA_PRODUCT_ID, 9].some((idProduto) => contractedProductIds.has(idProduto));
  const hasContentConnection = contractedProductIds.has(3);

  return {
    value: String(
      [hasCrmConnection, hasSiengeConnection, hasContentConnection].filter(Boolean).length
    ),
  };
};

const disableOperation = (operation) => ({
  ...operation,
  disabled: true,
  metrics: [],
});

const buildPreAttendanceOperation = (produtosContratados = []) => {
  const preAttendanceContract = produtosContratados.find(
    (produto) => getProdutoId(produto) === 2
  );

  if (!preAttendanceContract) return disableOperation(operations[0]);

  const attendances = toNumber(preAttendanceContract?.atendimentos ?? preAttendanceContract?.atendimentos_utilizados);
  const qualified = toNumber(preAttendanceContract?.leads_mes?.qualificados);

  return {
    ...operations[0],
    metrics: [
      ["Atendimentos", formatNumber(attendances)],
      ["Qualificados", formatNumber(qualified)],
    ],
  };
};

const buildBrokerOperation = (produtosContratados = []) => {
  const brokerContract = produtosContratados.find(
    (produto) => getProdutoId(produto) === 1
  );

  if (!brokerContract) {
    return disableOperation(operations[1]);
  }

  const people = toNumber(brokerContract?.pessoas_cadastradas?.total);
  const messages = toNumber(brokerContract?.mensagens_user_mes?.enviadas);

  return {
    ...operations[1],
    metrics: [
      ["Pessoas", formatNumber(people)],
      ["Mensagens", formatNumber(messages)],
    ],
  };
};

const buildPostSaleOperation = (produtosContratados = []) => {
  const postSaleContract = produtosContratados.find(
    (produto) => getProdutoId(produto) === 5
  );

  if (!postSaleContract) return disableOperation(operations[2]);

  const concluded = toNumber(postSaleContract?.atendimentos_mes?.concluidos);
  const open = toNumber(postSaleContract?.atendimentos_mes?.abertos);

  return {
    ...operations[2],
    metrics: [
      ["Atendimentos", formatNumber(concluded + open)],
      ["Concluídos", formatNumber(concluded)],
    ],
  };
};

const buildBillingOperation = (produtosContratados = []) => {
  const billingContract = produtosContratados.find(
    (produto) => getProdutoId(produto) === COBRANCA_PRODUCT_ID
  );

  return billingContract ? operations[3] : disableOperation(operations[3]);
};

const buildAccountingOperation = (produtosContratados = []) => {
  const accountingContract = produtosContratados.find(
    (produto) => getProdutoId(produto) === 9
  );

  if (!accountingContract) return disableOperation(operations[4]);

  return {
    ...operations[4],
    metrics: [
      ["Empresas analisadas", formatNumber(accountingContract?.empresas_analisadas)],
    ],
  };
};

const buildContentOperation = (produtosContratados = []) => {
  const contentContract = produtosContratados.find(
    (produto) => getProdutoId(produto) === 3
  );

  return contentContract ? operations[5] : disableOperation(operations[5]);
};

const buildOperationsData = (produtosContratados = []) => {
  const items = [
    buildPreAttendanceOperation(produtosContratados),
    buildBrokerOperation(produtosContratados),
    buildPostSaleOperation(produtosContratados),
    buildBillingOperation(produtosContratados),
    buildAccountingOperation(produtosContratados),
    buildContentOperation(produtosContratados),
  ];

  return [
    ...items.filter((item) => !item.disabled),
    ...items.filter((item) => item.disabled),
  ];
};

const summaryCards = [
  {
    label: "Operações ativas",
    value: "5",
    variant: "royal",
    description: "Pré Venda, Pós-Venda, Inadimplência, Conteúdo e Contabilidade.",
  },
  {
    label: "Agentes ativos",
    value: "4",
    variant: "purple",
    description: "Agentes liberados para uso na operação atual.",
  },
  {
    label: "Pré Venda",
    value: "0 / 3.250",
    description: "Consumo de atendimentos do mês.",
    progress: 0,
    variant: "green",
  },
  {
    label: "Pós-venda",
    value: "0 / 0",
    description: "Consumo de atendimentos do mês.",
    progress: 0,
    variant: "teal",
  },
  {
    label: "Conexões ativas",
    value: "3",
    variant: "navy",
    description: "Integrações ativas conforme produtos contratados.",
  },
];

const operations = [
  {
    title: "Pré Venda",
    owner: "Agente de Pré Venda",
    icon: TrackChangesOutlined,
    to: "/sdr",
    metrics: [
      ["Atendimentos", "1.248"],
      ["Qualificados", "312"],
    ],
  },
  {
    title: "Corretores",
    owner: "Agente do Corretor",
    icon: GroupsOutlined,
    to: "/assistente-vendas",
    metrics: [
      ["Mensagens", "42"],
      ["Pessoas", "12"],
    ],
  },
  {
    title: "Pós-Venda",
    owner: "Agente de Pós-Venda",
    icon: SupportAgentOutlined,
    to: "/assistente-pos-venda",
    metrics: [
      ["Atendimentos", "856"],
      ["Concluídos", "724"],
    ],
  },
  {
    title: "Inadimplência",
    owner: "Régua operacional",
    icon: AccountBalanceWalletOutlined,
    to: "/cobranca",
    metrics: [
      ["Msgs enviadas", "0"],
      ["Pessoas inadimplentes", "0"],
    ],
  },
  {
    title: "Contabilidade",
    owner: "Agente de Contabilidade",
    icon: BarChartRounded,
    to: "/agentecontabilidade",
    metrics: [
      ["Empresas analisadas", "0"],
    ],
  },
  {
    title: "Conteúdo",
    owner: "Agente de Conteúdo",
    icon: DescriptionOutlined,
    to: "/assistente-conteudo",
    metrics: [
      ["Mensagens", "0"],
      ["Pessoas", "0"],
    ],
  },
];

const accessRows = [
  ["Usuários ativos", "1"],
  ["Auditoria", "Disponível"],
];

const buildAccessRows = (snapshot) => [
  ["Usuários ativos", formatNumber(snapshot?.dados?.resumo?.usuarios_portal)],
  accessRows[1],
];

const SectionPanel = ({ children, sx }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: "8px",
      border: `1px solid ${colors.border}`,
      bgcolor: colors.panel,
      boxShadow: "var(--cc-shadow)",
      ...sx,
    }}
  >
    {children}
  </Paper>
);

const SummaryCard = ({ item }) => (
  <Paper
    elevation={0}
    sx={{
      minHeight: 106,
      borderRadius: "8px",
      p: 2,
      overflow: "hidden",
      position: "relative",
      color: "#fff",
      background: summaryGradients[item.variant] || summaryGradients.royal,
      border: "1px solid rgba(255,255,255,0.12)",
      boxShadow: "0 14px 28px rgba(15, 23, 42, 0.18)",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        width: 92,
        height: 92,
        borderRadius: "50%",
        top: -28,
        right: -22,
        bgcolor: "rgba(255,255,255,0.09)",
      }}
    />
    <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: 27, lineHeight: 1, position: "relative" }}>
      {item.value}
    </Typography>
    <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 13.5, mt: 0.7, position: "relative" }}>
      {item.label}
    </Typography>
    <Typography sx={{ color: "rgba(255,255,255,0.78)", fontSize: 12.2, lineHeight: 1.4, mt: 0.55, position: "relative" }}>
      {item.description}
    </Typography>
    {typeof item.progress === "number" && (
      <Box
        sx={{
          height: 7,
          borderRadius: 999,
          bgcolor: "rgba(255,255,255,0.16)",
          border: "1px solid rgba(255,255,255,0.18)",
          overflow: "hidden",
          mt: 1.25,
          position: "relative",
        }}
        aria-label={`Consumo ${item.progress}%`}
      >
        <Box
          sx={{
            width: `${Math.round(item.progress)}%`,
            height: "100%",
            bgcolor: "rgba(255,255,255,0.86)",
          }}
        />
      </Box>
    )}
  </Paper>
);

const PanelTitle = ({ title, subtitle }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography sx={{ color: colors.text, fontSize: 18, fontWeight: 900, lineHeight: 1.2 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography sx={{ color: colors.muted, fontSize: 12.4, lineHeight: 1.45, mt: 0.45 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

const EmptyPanelContent = ({ children = "Sem dados para exibir." }) => (
  <Box
    sx={{
      minHeight: 178,
      display: "grid",
      placeItems: "center",
      borderRadius: "8px",
      border: `1px dashed ${colors.border}`,
      bgcolor: colors.panelSoft,
      color: colors.muted,
      fontSize: 12.8,
      fontWeight: 700,
      textAlign: "center",
      px: 2,
    }}
  >
    {children}
  </Box>
);

const VolumeChart = ({ data }) => {
  const maxValue = Math.max(...data.map((item) => item.value), 0);

  return (
    <SectionPanel sx={{ p: { xs: 1.8, md: 2.1 }, minHeight: 312 }}>
      <PanelTitle
        title="Volume por operação no mês"
        subtitle="Comparativo por métrica principal de cada módulo."
      />
      {data.length === 0 ? (
        <EmptyPanelContent />
      ) : (
        <Stack spacing={1.25}>
          {data.map((item) => {
            const isContracted = item.contracted !== false;
            const width =
              typeof item.progress === "number"
                ? `${Math.max(Math.min(item.progress, 100), item.value > 0 ? 3 : 0)}%`
                : maxValue > 0
                  ? `${Math.max((item.value / maxValue) * 100, 5)}%`
                  : "0%";

            return (
              <Box key={item.label}>
                <Stack direction="row" justifyContent="space-between" spacing={1.5} sx={{ mb: 0.55 }}>
                  <Typography
                    sx={{
                      color: isContracted ? colors.text : colors.subtle,
                      fontSize: 12.8,
                      fontWeight: 800,
                      opacity: isContracted ? 1 : 0.56,
                    }}
                    noWrap
                  >
                    {item.label}
                  </Typography>
                  {isContracted && (
                    <Typography
                      sx={{
                        color: item.isOverLimit ? colors.danger : colors.muted,
                        fontSize: 12.5,
                        fontWeight: 750,
                        textAlign: "right",
                      }}
                    >
                      {item.display}
                    </Typography>
                  )}
                </Stack>
                {isContracted && (
                  <Box
                    sx={{
                      height: 11,
                      borderRadius: 999,
                      bgcolor: colors.chartTrack,
                      border: `1px solid ${colors.border}`,
                      overflow: "hidden",
                    }}
                    aria-label={`${item.label}: ${item.display}`}
                  >
                    <Box
                      sx={{
                        width,
                        height: "100%",
                        borderRadius: 999,
                        bgcolor: item.isOverLimit ? colors.danger : item.color,
                      }}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
    </SectionPanel>
  );
};

const FunnelChart = ({ data }) => {
  const maxValue = Math.max(...data.map((item) => item.value), 0);
  const isEmpty = data.length === 0;
  const qualificationRate = data.qualificationRate;

  return (
    <SectionPanel
      sx={{
        p: { xs: 1.8, md: 2.1 },
        minHeight: 312,
        bgcolor: isEmpty ? colors.panelSoft : colors.panel,
      }}
    >
      <PanelTitle title="Funil de Pré Venda" subtitle="Leitura rápida do mês atual." />
      {isEmpty ? (
        <EmptyPanelContent>Produto não contratado.</EmptyPanelContent>
      ) : (
        <Stack spacing={1.35} sx={{ mt: 2 }}>
          {data.map((item) => {
            const width =
              typeof item.progress === "number"
                ? `${Math.max(Math.min(item.progress, 100), item.value > 0 ? 3 : 0)}%`
                : maxValue > 0
                  ? `${item.value > 0 ? Math.max((item.value / maxValue) * 100, 10) : 0}%`
                  : "0%";

            return (
              <Box key={item.label}>
                <Stack direction="row" justifyContent="space-between" spacing={1.2} sx={{ mb: 0.55 }}>
                  <Typography sx={{ color: colors.text, fontSize: 13, fontWeight: 800 }}>
                    {item.label}
                  </Typography>
                  <Typography sx={{ color: colors.text, fontSize: 13, fontWeight: 900 }}>
                    {item.display}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    height: 14,
                    borderRadius: 999,
                    bgcolor: colors.chartTrack,
                    border: `1px solid ${colors.border}`,
                    overflow: "hidden",
                  }}
                  aria-label={`${item.label}: ${item.display}`}
                >
                  <Box sx={{ width, height: "100%", borderRadius: 999, bgcolor: item.color }} />
                </Box>
              </Box>
            );
          })}
          {typeof qualificationRate === "number" && (
            <Stack
              direction="row"
              justifyContent="space-between"
              spacing={1.2}
              sx={{
                pt: 1,
                color: colors.subtle,
                fontSize: 12.2,
                fontWeight: 750,
              }}
            >
              <Typography sx={{ color: "inherit", fontSize: "inherit", fontWeight: "inherit" }}>
                Taxa de Qualificação
              </Typography>
              <Typography sx={{ color: colors.success, fontSize: 13.2, fontWeight: 950 }}>
                {formatPercent(qualificationRate)}%
              </Typography>
            </Stack>
          )}
        </Stack>
      )}
    </SectionPanel>
  );
};

const PostSaleChart = ({ data }) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const concluded = data[0] || { label: "Concluídos", value: 0, color: colors.chartPrimary };
  const open = data[1] || { label: "Abertos", value: 0, color: colors.chartTrack };
  const concludedPercent = total > 0 ? Math.round((concluded.value / total) * 100) : 0;
  const chartBackground =
    total > 0
      ? `conic-gradient(${concluded.color} 0 ${concludedPercent}%, ${open.color} ${concludedPercent}% 100%)`
      : colors.chartTrack;

  return (
    <SectionPanel sx={{ p: { xs: 1.8, md: 2.1 }, minHeight: 312 }}>
      <PanelTitle title="Pós-Venda" subtitle="Chamados concluídos e abertos." />
      {data.length === 0 ? (
        <EmptyPanelContent>Produto não contratado.</EmptyPanelContent>
      ) : (
      <Stack spacing={1.8} alignItems="center" sx={{ mt: 1.5 }}>
        <Box
          sx={{
            width: 142,
            height: 142,
            borderRadius: "50%",
            background: chartBackground,
            display: "grid",
            placeItems: "center",
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.18)",
          }}
          aria-label={`${concluded.value} chamados concluídos e ${open.value} chamados abertos`}
        >
          <Box
            sx={{
              width: 88,
              height: 88,
              borderRadius: "50%",
              bgcolor: colors.panel,
              display: "grid",
              placeItems: "center",
              border: `1px solid ${colors.border}`,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ color: colors.text, fontWeight: 950, fontSize: 24, lineHeight: 1 }}>
                {concludedPercent}%
              </Typography>
              <Typography sx={{ color: colors.muted, fontWeight: 750, fontSize: 11.5, mt: 0.35 }}>
                concluídos
              </Typography>
            </Box>
          </Box>
        </Box>

        <Stack spacing={0.75} sx={{ width: "100%" }}>
          {data.map((item) => (
            <Stack key={item.label} direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
                <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: item.color, flexShrink: 0 }} />
                <Typography sx={{ color: colors.muted, fontSize: 12.5 }} noWrap>
                  Chamados {item.label.toLowerCase()}
                </Typography>
              </Stack>
              <Typography sx={{ color: colors.text, fontWeight: 900, fontSize: 13 }}>
                {item.value}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
      )}
    </SectionPanel>
  );
};

const OperationCard = ({ operation }) => {
  const Icon = operation.icon;
  const isDisabled = Boolean(operation.disabled);

  return (
    <Box
      component={isDisabled ? "div" : RouterLink}
      {...(!isDisabled ? { to: operation.to } : {})}
      aria-label={isDisabled ? `${operation.title} não contratado` : `Abrir ${operation.title}`}
      sx={{
        p: { xs: 1.55, lg: 1.35 },
        borderRadius: "8px",
        border: isDisabled ? `1px solid ${colors.border}` : `1px solid ${colors.borderStrong}`,
        bgcolor: isDisabled ? colors.panelSoft : colors.panelSoft,
        minHeight: { xs: 132, lg: 124 },
        display: "flex",
        flexDirection: "column",
        color: "inherit",
        textDecoration: "none",
        position: "relative",
        overflow: "hidden",
        transition: "border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease, background-color 160ms ease",
        opacity: isDisabled ? 0.38 : 1,
        cursor: isDisabled ? "default" : "pointer",
        ...(!isDisabled
          ? {
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                bgcolor: colors.primary,
              },
            }
          : {}),
        ...(isDisabled
          ? {}
          : {
              "&:hover": {
                borderColor: colors.borderStrong,
                bgcolor: colors.panel,
                boxShadow: "var(--cc-shadow)",
                transform: "translateY(-1px)",
              },
              "&:focus-visible": {
                outline: `2px solid ${colors.primary}`,
                outlineOffset: 2,
              },
            }),
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "8px",
              display: "grid",
              placeItems: "center",
              color: isDisabled ? colors.chartMuted : colors.primary,
              bgcolor: isDisabled ? colors.panelSoft : colors.primarySoft,
              border: `1px solid ${colors.border}`,
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 19 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: isDisabled ? colors.muted : colors.text, fontWeight: 900, fontSize: { xs: 17, lg: 16.4 }, lineHeight: 1.2 }}>
              {operation.title}
            </Typography>
            <Typography sx={{ color: colors.subtle, fontSize: { xs: 12.2, lg: 11.8 }, mt: 0.55, fontWeight: 700 }}>
              {operation.owner}
            </Typography>
          </Box>
        </Stack>
      </Stack>

      <Box sx={{ height: "1px", bgcolor: colors.border, mt: 1.1, mb: 0.9 }} />

      <Stack spacing={0.55}>
        {operation.metrics.map(([label, value]) => (
          <Stack key={label} direction="row" justifyContent="space-between" spacing={1.2} alignItems="baseline">
            <Typography sx={{ color: colors.muted, fontSize: { xs: 12.2, lg: 11.9 } }} noWrap>
              {label}
            </Typography>
            <Typography sx={{ color: colors.text, fontWeight: 850, fontSize: { xs: 12.5, lg: 12.2 }, textAlign: "right" }}>
              {value}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

const AccessPanel = ({ rows }) => (
  <SectionPanel sx={{ mt: 1.4, p: { xs: 1.8, md: 2 } }}>
    <Stack
      direction={{ xs: "column", md: "row" }}
      spacing={{ xs: 1.4, md: 2.2 }}
      alignItems={{ xs: "stretch", md: "center" }}
    >
      <Box sx={{ minWidth: { md: 250 }, maxWidth: { md: 340 } }}>
        <Typography sx={{ color: colors.text, fontSize: 18, fontWeight: 900 }}>
          Controle de acesso
        </Typography>
        <Typography sx={{ color: colors.muted, fontSize: 12.5, lineHeight: 1.5, mt: 0.45 }}>
          Perfis e permissões configurados para esta empresa.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          gap: 1,
          flex: 1,
        }}
      >
        {rows.map(([label, value]) => (
          <Stack
            key={label}
            direction="row"
            spacing={1.2}
            alignItems="center"
            justifyContent="space-between"
            sx={{
              px: 1.4,
              py: 1.15,
              borderRadius: "8px",
              border: `1px solid ${colors.border}`,
              bgcolor: colors.panelSoft,
            }}
          >
            <Typography sx={{ color: colors.text, fontSize: 13, fontWeight: 800 }} noWrap>
              {label}
            </Typography>
            <Typography sx={{ color: label === "Auditoria" ? colors.primary : colors.text, fontSize: 14, fontWeight: 900 }}>
              {value}
            </Typography>
          </Stack>
        ))}
      </Box>
    </Stack>
  </SectionPanel>
);

const HomeSnapshotLoading = () => (
  <Box sx={{ minHeight: "calc(100vh - 64px)", px: { xs: 2, sm: 3, lg: 4 }, py: { xs: 2.2, md: 2.8 } }}>
    <Stack spacing={1.4}>
      <Skeleton variant="text" width="min(440px, 80%)" height={52} />
      <Skeleton variant="text" width="min(620px, 92%)" height={26} />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
          gap: 1.15,
          mt: 1,
        }}
      >
        {[0, 1, 2, 3].map((item) => (
          <Skeleton key={item} variant="rounded" height={106} sx={{ borderRadius: "8px" }} />
        ))}
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1.35fr repeat(2, minmax(260px, 0.825fr))" },
          gap: 1.15,
        }}
      >
        {[0, 1, 2].map((item) => (
          <Skeleton key={item} variant="rounded" height={312} sx={{ borderRadius: "8px" }} />
        ))}
      </Box>
      <Skeleton variant="rounded" height={290} sx={{ borderRadius: "8px" }} />
    </Stack>
  </Box>
);

const HomeCommandCenter = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMountedRef = useRef(false);
  const [operationsSummaryCard, setOperationsSummaryCard] = useState(summaryCards[0]);
  const [agentsSummaryCard, setAgentsSummaryCard] = useState(summaryCards[1]);
  const [preAttendanceSummaryCard, setPreAttendanceSummaryCard] = useState(summaryCards[2]);
  const [postSaleSummaryCard, setPostSaleSummaryCard] = useState(summaryCards[3]);
  const [activeConnectionsSummaryCard, setActiveConnectionsSummaryCard] = useState(summaryCards[4]);
  const [contractedProductIds, setContractedProductIds] = useState([]);
  const [moduleVolumeData, setModuleVolumeData] = useState(moduleVolume);
  const [preAttendanceFunnelData, setPreAttendanceFunnelData] = useState(preAttendanceFunnel);
  const [postSaleTicketsData, setPostSaleTicketsData] = useState(postSaleTickets);
  const [operationsData, setOperationsData] = useState(operations);
  const [accessRowsData, setAccessRowsData] = useState(accessRows);
  const [referenceDateLabel, setReferenceDateLabel] = useState("Visão operacional consolidada");
  const [isRefreshingSnapshot, setIsRefreshingSnapshot] = useState(false);
  const [isLoadingSnapshot, setIsLoadingSnapshot] = useState(true);
  const [snapshotError, setSnapshotError] = useState("");

  const applyHomeSnapshot = useCallback((snapshot) => {
    const produtosContratados = Array.isArray(snapshot?.dados?.produtos_contratados)
      ? snapshot.dados.produtos_contratados
      : [];
    const contractedProductsSummary = buildContractedProductsSummary(produtosContratados);
    const resumo = snapshot?.dados?.resumo || {};
    const formattedReferenceDate = formatReferenceDate(snapshot?.dataHoraReferencia);

    setContractedProductIds(produtosContratados.map(getProdutoId));
    setOperationsSummaryCard({
      ...summaryCards[0],
      value: String(toNumber(resumo.operacoes_ativas)),
      description: contractedProductsSummary.description,
    });
    setAgentsSummaryCard({
      ...summaryCards[1],
      value: String(toNumber(resumo.agentes_ativos)),
    });
    setPreAttendanceSummaryCard({
      ...summaryCards[2],
      ...buildAttendanceSummaryByProduct(produtosContratados, 2),
    });
    setPostSaleSummaryCard({
      ...summaryCards[3],
      ...buildAttendanceSummaryByProduct(produtosContratados, 5),
    });
    setActiveConnectionsSummaryCard({
      ...summaryCards[4],
      ...buildActiveConnectionsSummary(produtosContratados),
    });
    setModuleVolumeData(buildVolumeByContractedProducts(produtosContratados));
    setPreAttendanceFunnelData(buildPreAttendanceFunnel(produtosContratados));
    setPostSaleTicketsData(buildPostSaleTickets(produtosContratados));
    setOperationsData(buildOperationsData(produtosContratados));
    setAccessRowsData(buildAccessRows(snapshot));
    setReferenceDateLabel(
      formattedReferenceDate
        ? `Atualizado em ${formattedReferenceDate}`
        : "Visão operacional consolidada"
    );
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    let isMounted = true;

    const fetchHomeSnapshot = async () => {
      const token = getAuthToken();
      const baseURL = process.env.REACT_APP_API_BASE_URL;

      if (!token || !baseURL) {
        if (isMounted) {
          setSnapshotError("Não foi possível carregar os dados da Central de Operações.");
          setIsLoadingSnapshot(false);
        }
        return;
      }

      try {
        const response = await axios.get(`${baseURL}/command-center/home`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!isMounted) return;

        const produtosContratados = Array.isArray(response?.data?.dados?.produtos_contratados)
          ? response.data.dados.produtos_contratados
          : [];
        const contractedProductsSummary = buildContractedProductsSummary(produtosContratados);
        const resumo = response?.data?.dados?.resumo || {};
        const formattedReferenceDate = formatReferenceDate(response?.data?.dataHoraReferencia);

        setContractedProductIds(produtosContratados.map(getProdutoId));
        setOperationsSummaryCard({
          ...summaryCards[0],
          value: String(toNumber(resumo.operacoes_ativas)),
          description: contractedProductsSummary.description,
        });
        setAgentsSummaryCard({
          ...summaryCards[1],
          value: String(toNumber(resumo.agentes_ativos)),
        });
        setPreAttendanceSummaryCard({
          ...summaryCards[2],
          ...buildAttendanceSummaryByProduct(produtosContratados, 2),
        });
        setPostSaleSummaryCard({
          ...summaryCards[3],
          ...buildAttendanceSummaryByProduct(produtosContratados, 5),
        });
        setActiveConnectionsSummaryCard({
          ...summaryCards[4],
          ...buildActiveConnectionsSummary(produtosContratados),
        });
        setModuleVolumeData(buildVolumeByContractedProducts(produtosContratados));
        setPreAttendanceFunnelData(buildPreAttendanceFunnel(produtosContratados));
        setPostSaleTicketsData(buildPostSaleTickets(produtosContratados));
        setOperationsData(buildOperationsData(produtosContratados));
        setAccessRowsData(buildAccessRows(response?.data));
        setReferenceDateLabel(
          formattedReferenceDate
            ? `Atualizado em ${formattedReferenceDate}`
            : "Visão operacional consolidada"
        );
      } catch (error) {
        if (isMounted) {
          setSnapshotError("Não foi possível carregar os dados da Central de Operações.");
        }
      } finally {
        if (isMounted) setIsLoadingSnapshot(false);
      }
    };

    fetchHomeSnapshot();

    return () => {
      isMounted = false;
      isMountedRef.current = false;
    };
  }, []);

  const handleRefreshSnapshot = useCallback(async () => {
    const token = getAuthToken();
    const baseURL = process.env.REACT_APP_API_BASE_URL;

    if (!token || !baseURL || isRefreshingSnapshot) return;

    setIsRefreshingSnapshot(true);

    try {
      const headers = { Authorization: `Bearer ${token}` };

      await axios.post(`${baseURL}/command-center/home/refresh`, null, { headers });

      for (let attempt = 0; attempt < refreshPollingMaxAttempts; attempt += 1) {
        await wait(refreshPollingIntervalMs);

        const statusResponse = await axios.get(`${baseURL}/command-center/home/status`, { headers });

        if (!statusResponse?.data?.processando) break;
      }

      const homeResponse = await axios.get(`${baseURL}/command-center/home`, { headers });
      if (isMountedRef.current) applyHomeSnapshot(homeResponse?.data);
    } catch (error) {
      // Mantem os dados atuais se a atualizacao falhar.
    } finally {
      if (isMountedRef.current) setIsRefreshingSnapshot(false);
    }
  }, [applyHomeSnapshot, isRefreshingSnapshot]);

  const currentSummaryCards = [
    operationsSummaryCard,
    agentsSummaryCard,
    ...(contractedProductIds.includes(2) ? [preAttendanceSummaryCard] : []),
    ...(contractedProductIds.includes(5) ? [postSaleSummaryCard] : []),
    activeConnectionsSummaryCard,
  ];

  if (isLoadingSnapshot) {
    return <HomeSnapshotLoading />;
  }

  if (snapshotError) {
    return (
      <Box sx={{ minHeight: "calc(100vh - 64px)", display: "grid", placeItems: "center", px: 3 }}>
        <Typography color="text.secondary">{snapshotError}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        "--cc-page": isDark ? "#050817" : "#f6f8fb",
        "--cc-panel": isDark ? "rgba(8, 13, 28, 0.9)" : "rgba(255, 255, 255, 0.92)",
        "--cc-panel-soft": isDark ? "rgba(12, 19, 38, 0.74)" : "rgba(248, 250, 252, 0.9)",
        "--cc-border": isDark ? "rgba(148, 163, 184, 0.16)" : "rgba(15, 23, 42, 0.1)",
        "--cc-border-strong": isDark ? "rgba(148, 163, 184, 0.26)" : "rgba(15, 23, 42, 0.18)",
        "--cc-text": isDark ? "#f8fafc" : "#0f172a",
        "--cc-muted": isDark ? "#b7c0d3" : "#475569",
        "--cc-subtle": isDark ? "#8994aa" : "#64748b",
        "--cc-primary": isDark ? "#a78bfa" : "#6d28d9",
        "--cc-primary-soft": isDark ? "rgba(124, 58, 237, 0.16)" : "rgba(109, 40, 217, 0.08)",
        "--cc-chart-primary": isDark ? "#a78bfa" : "#6d28d9",
        "--cc-chart-secondary": isDark ? "#93c5fd" : "#6384d8",
        "--cc-chart-muted": isDark ? "#64748b" : "#94a3b8",
        "--cc-chart-track": isDark ? "rgba(100, 116, 139, 0.22)" : "#e8edf4",
        "--cc-danger": isDark ? "#f87171" : "#dc2626",
        "--cc-success": isDark ? "#4ade80" : "#15803d",
        "--cc-shadow": isDark ? "0 18px 48px rgba(0, 0, 0, 0.22)" : "0 18px 42px rgba(15, 23, 42, 0.06)",
        mx: { xs: -2, sm: -3 },
        my: -3,
        minHeight: "calc(100vh - 64px)",
        color: colors.text,
        bgcolor: colors.page,
        position: "relative",
        overflow: "hidden",
        px: { xs: 2, sm: 3, lg: 4 },
        py: { xs: 2.2, md: 2.8 },
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          backgroundImage: isDark
            ? `linear-gradient(90deg, rgba(5, 8, 23, 0.98) 0%, rgba(5, 8, 23, 0.9) 42%, rgba(5, 8, 23, 0.78) 100%), url(${commandBackground})`
            : `linear-gradient(90deg, rgba(246, 248, 251, 0.99) 0%, rgba(246, 248, 251, 0.94) 48%, rgba(246, 248, 251, 0.86) 100%), url(${commandBackground})`,
          backgroundSize: "cover",
          backgroundPosition: { xs: "center top", lg: "center top" },
          opacity: isDark ? 0.72 : 0.22,
          filter: isDark ? "saturate(0.76)" : "saturate(0.4)",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: isDark
            ? "linear-gradient(180deg, rgba(5, 8, 23, 0.18) 0%, rgba(5, 8, 23, 0.82) 62%, #050817 100%)"
            : "linear-gradient(180deg, rgba(246, 248, 251, 0.25) 0%, rgba(246, 248, 251, 0.86) 62%, #f6f8fb 100%)",
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 0.8, md: 2 }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "flex-end" }}
          sx={{ mb: 1.7 }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "baseline",
              columnGap: { xs: 1, sm: 1.4 },
              rowGap: 0.4,
            }}
          >
            <Typography
              component="h1"
              sx={{
                color: colors.text,
                fontWeight: 900,
                fontSize: { xs: 26, sm: 30, lg: 34 },
                lineHeight: 1.08,
                whiteSpace: { xs: "normal", lg: "nowrap" },
              }}
            >
              Central de Operações
            </Typography>
            <Typography
              sx={{
                color: colors.subtle,
                fontWeight: 750,
                fontSize: { xs: 13, md: 14 },
                lineHeight: 1.25,
              }}
            >
              Visão consolidada dos agentes e módulos contratados
            </Typography>
          </Box>

          <Stack direction="row" spacing={0.45} alignItems="center">
            <Typography sx={{ color: colors.subtle, fontSize: 12.5, fontWeight: 750, whiteSpace: "nowrap" }}>
              {referenceDateLabel}
            </Typography>
            <Tooltip title={isRefreshingSnapshot ? "Atualizando dados" : "Atualizar dados"}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleRefreshSnapshot}
                  disabled={isRefreshingSnapshot}
                  aria-label="Atualizar dados da Central de Operações Aipim"
                  sx={{
                    width: 28,
                    height: 28,
                    color: colors.subtle,
                    border: `1px solid ${colors.border}`,
                    bgcolor: colors.panelSoft,
                    "&:hover": {
                      color: colors.primary,
                      bgcolor: colors.primarySoft,
                    },
                  }}
                >
                  {isRefreshingSnapshot ? (
                    <CircularProgress size={14} thickness={5} sx={{ color: colors.primary }} />
                  ) : (
                    <RefreshRounded sx={{ fontSize: 16 }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: `repeat(${currentSummaryCards.length}, minmax(0, 1fr))`,
            },
            gap: 1.15,
            mb: 1.35,
          }}
        >
          {currentSummaryCards.map((item) => (
            <SummaryCard key={item.label} item={item} />
          ))}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              lg: "minmax(0, 1.35fr) repeat(2, minmax(260px, 0.825fr))",
            },
            gap: 1.15,
            mb: 1.35,
          }}
        >
          <VolumeChart data={moduleVolumeData} />
          <FunnelChart data={preAttendanceFunnelData} />
          <PostSaleChart data={postSaleTicketsData} />
        </Box>

        <SectionPanel sx={{ p: { xs: 1.8, md: 2.1 } }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1}
            alignItems={{ xs: "flex-start", md: "baseline" }}
            justifyContent="space-between"
            sx={{ mb: 1.45 }}
          >
            <Box>
              <Typography sx={{ color: colors.text, fontSize: 21, fontWeight: 900 }}>
                Operações monitoradas
              </Typography>
              <Typography sx={{ color: colors.muted, fontSize: 12.8, mt: 0.35 }}>
                Acesso rápido aos módulos e seus principais indicadores.
              </Typography>
            </Box>
            <Typography sx={{ color: colors.subtle, fontSize: 12.2, fontWeight: 750 }}>
              Dados consolidados da operação atual
            </Typography>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" },
              gap: 1.05,
            }}
          >
            {operationsData.length > 0 ? (
              operationsData.map((operation) => (
                <OperationCard key={operation.title} operation={operation} />
              ))
            ) : (
              <Box sx={{ gridColumn: "1 / -1" }}>
                <EmptyPanelContent>Sem operações para exibir.</EmptyPanelContent>
              </Box>
            )}
          </Box>
        </SectionPanel>

        <AccessPanel rows={accessRowsData} />
      </Box>
    </Box>
  );
};

export default HomeCommandCenter;
